import React, { useState } from 'react';
import { Theme, Grid, Typography } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';

import PaymentRow from '../PaymentRow';
import { CurrencyObject, OrderObject, OrderPayment } from 'common/types';
import { useDispatch, useSelector } from 'react-redux';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as StockSelectors from 'selectors/StockSelectors';
import * as RentalViewSelectors from 'selectors/RentalViewSelectors';
import PaymentStatus from '../components/PaymentStatus';
import { getPricingString } from 'common/utils/common';
import { getChargeStatus } from './utils';
import { useTranslation } from 'services/localization/useTranslation';
import { getPaymentAmountsFromOrderPayment } from 'common/modules/atoms/paymentAmounts';
import {
	getExternalPosTriggerTypes,
	getPosIntegrationType,
	handlePosIntegrationAction,
} from 'common/modules/posIntegrations';
import PaymentButton, { ChargeButtonType } from '../components/PaymentButton';
import * as NotificationActions from 'actions/NotificationActions';
import * as RentalViewActions from 'actions/RentalViewActions';
import { getResponsiblePersonDetails, newFirestoreId } from 'common/utils/newRentalUtils';
import { addOrderPaymentRequest, getNewPaymentNumber } from 'common/api/frontend';
import { getReceiptBarCode } from 'services/utils/receipt';
import { getNewOrderNumber } from 'common/api/db/functions';
import ManualPaymentMethodSelect from 'components/ManualPaymentMethodSelect';
import {
	getPaymentMethodId,
	isManualShopPaymentMethodObject,
	shopHasPaymentMethod,
} from 'common/modules/payments/paymentMethods';
import { LocaleHelpers } from 'common/locales/helpers';
import { ShopManualOnlinePaymentMethodObject } from 'common/modules/payments/types';
import { useHasFeature } from 'hooks';
import { getChargeStatusTranslation } from 'services/translations';

interface Props {
	order: OrderObject;
	payment: OrderPayment;
	total: number;
	additionalPricingInfo?: string;
	currency: CurrencyObject;
	markAsPaid: () => void;
	showPayDialog: () => void;
}

const ChargeSection = ({
	payment,
	total: _total,
	currency,
	markAsPaid,
	showPayDialog,
	additionalPricingInfo,
	order,
}: Props) => {
	const { rentalInfo, products, shoppers, orderDelivery } = order;
	const classes = useStyles();
	const dispatch = useDispatch();
	const { hasFeature } = useHasFeature();
	const categories = useSelector(StockSelectors.categoriesData);
	const useAsPos = useSelector(ShopSelectors.getUseAsPos) && hasFeature('POS');
	const shopReadOnly = useSelector(ShopSelectors.activeShopReadOnly);
	const shopInfo = useSelector(ShopSelectors.activeShopInfo)!;
	const activeManualPaymentMethodOptions = useSelector(
		ShopSelectors.getActiveManualPaymentMethodOptions,
	);
	const selectedPaymentMethod = useSelector(RentalViewSelectors.paymentMethod);

	const externalPosTriggerTypes = getExternalPosTriggerTypes({
		readOnly: shopReadOnly,
		shopInfo,
	});
	const externalPosPaymentButtonInUse = externalPosTriggerTypes.includes('externalButton');
	const [paymentButtonLoading, setPaymentButtonLoading] = useState<boolean>(false);
	const paymentAmounts = getPaymentAmountsFromOrderPayment(_total, currency.code, payment);
	const posType = getPosIntegrationType(shopReadOnly);

	const { t } = useTranslation();
	const chargeStatus = getChargeStatus(paymentAmounts);

	const { total, paid, unpaid } = paymentAmounts;

	const getPaymentButtonTypes = (): ChargeButtonType[] => {
		const cloudPosButtonInUse = externalPosPaymentButtonInUse && posType === 'CLOUD_POS';
		const teksoButtonInUse = externalPosPaymentButtonInUse && posType === 'TEKSO';
		const types: ChargeButtonType[] = [];
		if (cloudPosButtonInUse) types.push('SEND_PAYMENT_TO_CLOUD_POS');
		if (teksoButtonInUse) types.push('SEND_PAYMENT_TO_TEKSO_POS');
		types.push(useAsPos ? 'RENTLE_POS_PAY' : 'MARK_AS_PAID');
		return types;
	};

	const handlePaymentButtonClick = async (type: ChargeButtonType) => {
		switch (type) {
			case 'MARK_AS_PAID':
				return markAsPaid();
			case 'RENTLE_POS_PAY':
				return showPayDialog();
			case 'SEND_PAYMENT_TO_TEKSO_POS':
			case 'SEND_PAYMENT_TO_CLOUD_POS':
				setPaymentButtonLoading(true);
				await sendPosTrigger();
				dispatch(
					NotificationActions.showNotification({
						variant: 'info',
						message: `${t('actions.sentToPos', 'Sent to POS')}`,
						autoDismissMs: 2000,
					}),
				);
				setPaymentButtonLoading(false);
				return null;
			default:
				return null;
		}
	};

	const sendPosTrigger = async () => {
		const newPaymentNumber = await getNewPaymentNumber(rentalInfo.shopId);
		const orderNumber =
			rentalInfo.orderNumber ??
			(await getNewOrderNumber({ orderId: rentalInfo.id, shopId: rentalInfo.shopId }));
		const receiptBarCode = getReceiptBarCode({
			readOnlyData: shopReadOnly,
			shopInfo,
			paymentNumber: newPaymentNumber,
			amount: unpaid,
			orderId: rentalInfo.id,
			receiptType: null,
			orderNumber,
		});

		const paymentRequestId = newFirestoreId();
		const responsiblePerson = getResponsiblePersonDetails(rentalInfo.responsiblePerson, shoppers);
		await addOrderPaymentRequest({
			id: paymentRequestId,
			shopId: shopInfo!.id,
			orderId: rentalInfo.id,
			amount: {
				value: unpaid,
				currency: rentalInfo.pricing.currency,
			},
			paymentNumber: newPaymentNumber,
			shopperIds: shoppers.map((s) => s.id),
			productIds: products.map((p) => p.id),
			orderDeliveryId: orderDelivery?.id,
			paid: false,
		});
		await handlePosIntegrationAction(
			{
				shopId: shopInfo!.id,
				shopReadOnly,
				locationId: rentalInfo.startLocation.id,
				customer: {
					firstName: responsiblePerson.firstName,
					lastName: responsiblePerson.lastName,
				},
				orderDate: rentalInfo.startDate,
				receiptProducts: products,
				barCode: receiptBarCode,
				paymentNumber: newPaymentNumber,
				paymentRequestId,
				categories,
			},
			t,
		);
	};

	const showManualPaymentSelector = (
		methods: ShopManualOnlinePaymentMethodObject[],
		selectedPaymentMethod: ShopManualOnlinePaymentMethodObject,
	) => {
		return (
			methods.length > 1 ||
			(methods.length === 1 &&
				!shopHasPaymentMethod(getPaymentMethodId(selectedPaymentMethod), methods))
		);
	};

	return (
		<>
			<PaymentStatus
				variant={chargeStatus.variant}
				statusText={getChargeStatusTranslation(chargeStatus.status, t)}
				dataQA="paymentStatus"
				rightActionBtn={
					!!selectedPaymentMethod && isManualShopPaymentMethodObject(selectedPaymentMethod) ? (
						<ManualPaymentMethodSelect
							options={activeManualPaymentMethodOptions}
							value={selectedPaymentMethod}
							onChange={async (method) => {
								dispatch(
									RentalViewActions.updateActivePartialOrder({
										rentalInfo: {
											id: rentalInfo.id,
											data: {
												pricing: {
													...rentalInfo.pricing,
													paymentMethod: method,
												},
											},
										},
									}),
								);
							}}
							variant={
								showManualPaymentSelector(activeManualPaymentMethodOptions, selectedPaymentMethod)
									? 'link'
									: 'text'
							}
						/>
					) : !!selectedPaymentMethod ? (
						<Typography variant="body2" color="secondary">
							{LocaleHelpers.admin(t).paymentMethods[selectedPaymentMethod.id] ??
								selectedPaymentMethod.id}
						</Typography>
					) : undefined
				}
			/>
			<div className={classes.paymentDetailContainer}>
				<div className={classes.paymentRowContainer}>
					<PaymentRow
						description={t('common:payment.total')}
						value={getPricingString(total, currency)}
					/>
					{paid !== total && !!paid && (
						<PaymentRow
							description={t('common:payment.paid', 'Paid')}
							value={getPricingString(paid, currency)}
						/>
					)}
					{unpaid !== total && !!unpaid && (
						<PaymentRow
							description={
								unpaid < 0
									? t('common:payment.toBeRefunded', 'To be refunded')
									: t('common:payment.unpaid', 'Unpaid')
							}
							value={getPricingString(Math.abs(unpaid), currency)}
							variant="subtitle2"
							dataQA="RentalCard--unpaidAmount"
						/>
					)}
					{additionalPricingInfo && (
						<PaymentRow description={t('common:form.info')} value={additionalPricingInfo} />
					)}
				</div>
			</div>
			<Grid container justifyContent="center" alignItems="center">
				{unpaid > 0 && (
					<Grid item xs={12}>
						<PaymentButton
							types={getPaymentButtonTypes()}
							onClick={handlePaymentButtonClick}
							loading={paymentButtonLoading}
							variant="contained"
						/>
					</Grid>
				)}
			</Grid>
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {
			padding: theme.spacing(1, 0),
		},
		paymentIndicator: {
			width: 16,
			height: 16,
			borderRadius: 30,
			marginRight: theme.spacing(2),
		},
		paymentDetailContainer: {
			maxHeight: 150,
			display: 'flex',
			flexDirection: 'column',
			paddingBottom: theme.spacing(2),
		},
		paymentRowContainer: {
			flexGrow: 1,
			padding: '4px 0',
		},
		leftIcon: {
			marginRight: theme.spacing(1),
		},
	}),
);

export default ChargeSection;
